/**
 * HotspotDetail — W4.7 / B8.1
 *
 * Power-User-Ansicht analog v1: alle Daten auf einer Seite.
 * Grid-Layout: Hero + Karte + Chart + 4 Info-Cards + PoC + Speedtests (unten).
 *
 * Vorgänger (W1.3): Tab-Switcher Performance/Speedtests.
 * Neu: Alle Daten auf einer Seite. Speedtests als optionale Section unten.
 * B8.1: "Trennen"-Button im Hero mit Bestätigungs-Dialog.
 */
import { useState, useEffect } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { setPublicPageMeta, publicUrl } from '../../lib/seo'
import { toast } from '../../components/Toast/useToast'
import { useHotspotDetail } from './useHotspotDetail'
import { hotspotDisplayName } from '../../lib/hotspotDisplayName'
import { useRemoveHotspot } from './useRemoveHotspot'
import { useUpdateHotspot } from './useUpdateHotspot'
import { useAddHotspot } from './useAddHotspot'
import { HotspotHeroBar } from './HotspotHeroBar'
import { HotspotStat3 } from './HotspotStat3'
import { HotspotLocationCard } from './HotspotLocationCard'
import { HotspotActivityCard } from './HotspotActivityCard'
import { HotspotGuardToggle } from './HotspotGuardToggle'
import { GuardStatusBadge } from './GuardStatusBadge'
import { HeartbeatHistoryCard } from './HeartbeatHistoryCard'
import { SpeedtestsTab } from './SpeedtestsTab'
import { InlineNotesSection } from './InlineNotesSection'
import { HotspotRewardsChart } from './HotspotRewardsChart'
import { HotspotHeatmapCard } from './HotspotHeatmapCard'
import { HotspotSplitsCard } from './HotspotSplitsCard'
import { TagPicker } from '../tags/TagPicker'
import { useTags } from '../tags/useTags'
import { HotspotStatusChips } from './HotspotStatusChips'
import { DataTransferTab } from './DataTransferTab'
import { PocArchiveTab } from './PocArchiveTab'
import './hotspot-detail-tabs.css'

// --- Detail-Tabs (Stories 1.5 + 1.6) ---
// Normalisierung + Aliase (inkl. Deep-Link-Alias "poc-archive", BUG 1) leben
// in detailTab.ts, damit diese Datei nur Komponenten exportiert.
import { normalizeDetailTab, OWNER_ONLY_TABS, type DetailTab } from './detailTab'

// --- Skeleton ---

function DetailSkeleton() {
  const { t } = useTranslation('hotspots')
  return (
    <div
      className="hotspot-detail-skeleton"
      aria-busy="true"
      aria-label={t('detail.skeletonLabel')}
    >
      <div className="skeleton" style={{ height: 130, marginBottom: 16 }} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="skeleton" style={{ height: 320 }} />
        <div className="skeleton" style={{ height: 320 }} />
      </div>
    </div>
  )
}

// --- HotspotDetail ---

type Props = {
  id: string
  // tab wird für Abwärtskompatibilität mit Route akzeptiert, aber nicht mehr als Tab genutzt
  tab?: string
}

// --- Bestätigungs-Dialog ---

type RemoveConfirmDialogProps = {
  hotspotName: string | null
  isPending: boolean
  onConfirm: () => void
  onCancel: () => void
}

function RemoveConfirmDialog({
  hotspotName,
  isPending,
  onConfirm,
  onCancel,
}: RemoveConfirmDialogProps) {
  const { t } = useTranslation('hotspots')
  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="remove-hotspot-title"
      data-testid="remove-hotspot-dialog"
    >
      <div className="modal">
        <div className="modal-header">
          <h2 id="remove-hotspot-title">{t('remove.title')}</h2>
        </div>
        <div className="modal-body">
          <p>
            {hotspotName
              ? t('remove.confirmMsg', { name: hotspotName })
              : t('remove.confirmMsgFallback')}
          </p>
          <p className="muted" style={{ fontSize: 13 }}>
            {t('remove.noteMsg')}
          </p>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={isPending}>
            {t('remove.cancelBtn')}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={onConfirm}
            disabled={isPending}
            data-testid="remove-hotspot-confirm"
          >
            {isPending ? t('remove.pendingBtn') : t('remove.confirmBtn')}
          </button>
        </div>
      </div>
    </div>
  )
}

// --- Edit-Dialog ---

type EditDialogProps = {
  initialTagList: string
  initialWatched: boolean
  initialAffiliate: boolean
  isPending: boolean
  onSubmit: (values: { tag_list: string; watched: boolean; affiliate: boolean }) => void
  onCancel: () => void
}

function EditHotspotDialog({
  initialTagList,
  initialWatched,
  initialAffiliate,
  isPending,
  onSubmit,
  onCancel,
}: EditDialogProps) {
  const { t } = useTranslation('hotspots')
  const [tagList, setTagList] = useState(initialTagList)
  const [watched, setWatched] = useState(initialWatched)
  const [affiliate, setAffiliate] = useState(initialAffiliate)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    onSubmit({ tag_list: tagList, watched, affiliate })
  }

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-hotspot-title"
      data-testid="edit-hotspot-modal"
    >
      <div className="modal">
        <div className="modal-header">
          <h2 id="edit-hotspot-title">{t('edit.title')}</h2>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label
                htmlFor="edit-tag-list"
                style={{ display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 500 }}
              >
                {t('edit.tagsLabel')}{' '}
                <span className="muted" style={{ fontWeight: 400 }}>
                  ({t('edit.tagsHint')})
                </span>
              </label>
              <input
                id="edit-tag-list"
                type="text"
                data-testid="edit-field-tag-list"
                value={tagList}
                onChange={(e) => setTagList(e.target.value)}
                placeholder={t('edit.tagsPlaceholder')}
                style={{ width: '100%', boxSizing: 'border-box' }}
                disabled={isPending}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                id="edit-watched"
                type="checkbox"
                data-testid="edit-field-watched"
                checked={watched}
                onChange={(e) => setWatched(e.target.checked)}
                disabled={isPending}
              />
              <label htmlFor="edit-watched" style={{ fontSize: 13 }}>
                {t('edit.watchedLabel')}
              </label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                id="edit-affiliate"
                type="checkbox"
                data-testid="edit-field-affiliate"
                checked={affiliate}
                onChange={(e) => setAffiliate(e.target.checked)}
                disabled={isPending}
              />
              <label htmlFor="edit-affiliate" style={{ fontSize: 13 }}>
                {t('edit.affiliateLabel')}
              </label>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-ghost"
              data-testid="edit-cancel-button"
              onClick={onCancel}
              disabled={isPending}
            >
              {t('edit.cancelBtn')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              data-testid="edit-submit-button"
              disabled={isPending}
            >
              {isPending ? t('edit.savingBtn') : t('edit.saveBtn')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export function HotspotDetail({ id, tab }: Props) {
  const { t } = useTranslation('hotspots')
  const { t: tTabs } = useTranslation('hotspotDetailTabs')
  const navigate = useNavigate()
  // Stories 1.5 + 1.6: Tab-State (Default „Data Transfer"). Legacy-Werte des
  // tab-Suchparams werden auf den Rewards-Tab gemappt.
  const [activeTab, setActiveTab] = useState<DetailTab>(() => normalizeDetailTab(tab))

  // W3-F5: Tab-State <-> URL bidirektional.
  // Rein: Deep-Link/Back-Navigation setzt den Tab (Legacy-Werte werden weiter
  // normalisiert). Raus: ein Tabwechsel schreibt den kanonischen Tab-Namen
  // per replace in ?tab= zurueck, damit Reload und Teilen den Tab erhalten
  // und keine History-Eintraege pro Klick entstehen.
  // Rein-Richtung als "adjusting state during render" (React-Doku-Pattern statt
  // setState-im-Effect): reagiert ohne Zwischenrender mit veraltetem Tab.
  const normalizedTab = normalizeDetailTab(tab)
  const [lastSearchTab, setLastSearchTab] = useState<DetailTab>(normalizedTab)
  if (normalizedTab !== lastSearchTab) {
    setLastSearchTab(normalizedTab)
    setActiveTab(normalizedTab)
  }

  function handleTabChange(next: DetailTab) {
    setActiveTab(next)
    // heliumtracker-web#6: TanStack Router setzt bei jeder Navigation
    // (auch bei einem reinen Search-Param-Replace) per Default den Scroll
    // auf (0,0) zurueck (resetScroll default true). Der Tab-Wechsel bleibt
    // auf derselben Seite — resetScroll: false verhindert, dass der Router
    // zusaetzlich die gesamte Seite hart zuruecksetzt.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(navigate as any)({
      to: '/hotspots/$id',
      params: { id },
      search: { tab: next },
      replace: true,
      resetScroll: false,
    })
    // heliumtracker-web#6: Ohne eigenen Scroll-Reset bleibt der Scroll-Offset des
    // vorherigen (oft laengeren) Panels stehen. Ist das neue Panel kuerzer, ragt
    // der alte Offset ueber das Dokumentende hinaus — der Browser springt dann
    // unkontrolliert auf die neue maximale Scroll-Position. Der neue Panel-
    // Container wird daher gezielt nach oben gescrollt (nach dem Re-Render, daher
    // im rAF), respektiert dabei prefers-reduced-motion.
    // suite#37: Nur scrollen, wenn das Panel-Top tatsaechlich ausserhalb des
    // sichtbaren Viewports liegt — sonst springt die Seite bei jedem Tabwechsel
    // unnoetig nach oben, obwohl das Panel bereits sichtbar ist.
    requestAnimationFrame(() => {
      const panel = document.getElementById(`hotspot-panel-${next}`)
      if (!panel) return
      const top = panel.getBoundingClientRect().top
      const isOutsideViewport = top < 0 || top >= window.innerHeight
      if (!isOutsideViewport) return
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      panel.scrollIntoView?.({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' })
    })
  }

  const { data: hotspot, isLoading, isError, refetch } = useHotspotDetail(id)
  // Dynamischer SEO-Titel sobald Hotspot-Daten vorliegen.
  //
  // Seit die Seite wieder oeffentlich ist (09.2026), kommen Canonical, Open
  // Graph und JSON-LD dazu. Das Canonical zeigt immer auf die ID-Form ohne
  // Query-Parameter — sonst zaehlten `/hotspots/12`, `/hotspots/12?tab=rewards`
  // und die Adress-Variante als drei Seiten mit identischem Inhalt.
  useEffect(() => {
    if (!hotspot) return
    const name = hotspotDisplayName(
      hotspot.name,
      hotspot.address,
      t('detail.fallbackHeliumHotspot'),
    )
    const canonical = publicUrl(`/hotspots/${hotspot.id ?? id}`)

    return setPublicPageMeta({
      title: t('detail.seoTitle', { name }),
      description: hotspot.city_name
        ? t('detail.seoDescriptionWithCity', { name, city: hotspot.city_name })
        : t('detail.seoDescription', { name }),
      canonical,
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'Place',
        name,
        url: canonical,
        ...(hotspot.city_name || hotspot.country
          ? {
              address: {
                '@type': 'PostalAddress',
                ...(hotspot.city_name ? { addressLocality: hotspot.city_name } : {}),
                ...(hotspot.country ? { addressCountry: hotspot.country } : {}),
              },
            }
          : {}),
        ...(hotspot.lat != null && hotspot.lng != null
          ? { geo: { '@type': 'GeoCoordinates', latitude: hotspot.lat, longitude: hotspot.lng } }
          : {}),
      },
    })
  }, [hotspot, id, t])
  const { data: allTags } = useTags()
  const removeHotspot = useRemoveHotspot()
  const updateHotspot = useUpdateHotspot(id)
  const addHotspot = useAddHotspot()
  const [showRemoveDialog, setShowRemoveDialog] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [shareCopied, setShareCopied] = useState(false)

  // Issue web#97: v1 zeigte JEDEN Hotspot, v2 lieferte fuer fremde 404.
  // Fix: die Detailseite ist jetzt fuer alle Hotspots lesbar — Notizen/Tags/
  // Splits/Bearbeiten/Entfernen/Benachrichtigungen bleiben besitzerexklusiv.
  // is_owner fehlt nur bei aelteren Mock-/Test-Fixtures ohne das Feld → dann
  // wie bisher (eigener Hotspot) behandeln.
  const isOwner = hotspot?.is_owner !== false
  // Deep-Link/vorheriger Tab-Zustand kann bei fremden Hotspots auf einen
  // besitzergebundenen Tab zeigen (z.B. ?tab=notes) — dann auf "rewards"
  // ausweichen, ohne den activeTab-State selbst zu veraendern.
  const visibleTab: DetailTab =
    !isOwner && OWNER_ONLY_TABS.includes(activeTab) ? 'rewards' : activeTab

  const hotspotNumericId = hotspot?.id ?? Number(id)
  const assignedTagIds = (allTags ?? [])
    .filter(
      (t) =>
        t.hotspot_ids?.includes(hotspotNumericId) ??
        t.hotspots?.some((h) => h.id === hotspotNumericId),
    )
    .map((t) => t.id)

  async function handleRemove() {
    await removeHotspot.mutateAsync(id)
    setShowRemoveDialog(false)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(navigate as any)({ to: '/hotspots' })
  }

  async function handleEditSubmit(values: {
    tag_list: string
    watched: boolean
    affiliate: boolean
  }) {
    await updateHotspot.mutateAsync({
      tag_list: values.tag_list || null,
      watched: values.watched,
      affiliate: values.affiliate,
    })
    setShowEditDialog(false)
  }

  async function handleShare() {
    const url = `${window.location.origin}/hotspots/${id}`

    if (navigator.share) {
      try {
        await navigator.share({
          title: hotspot
            ? hotspotDisplayName(hotspot.name, hotspot.address, t('detail.fallbackName'))
            : t('detail.fallbackName'),
          url,
        })
        return
      } catch {
        // Nutzer hat abgebrochen oder Web-Share nicht unterstützt — Fallback auf Clipboard
      }
    }

    // BUGFIX (onboarding-wallet-hotspot #7): navigator.clipboard.writeText kann
    // ohne Nutzer-Geste bzw. ohne Clipboard-Berechtigung ablehnen (z.B. in
    // automatisierten Browsern) — das rief zuvor eine ungefangene Exception hervor,
    // der Button wirkte dadurch "tot" (kein sichtbares Feedback). Jetzt mit
    // try/catch + Toast-Feedback in beiden Faellen.
    try {
      await navigator.clipboard.writeText(url)
      setShareCopied(true)
      toast.success(t('detail.shareLinkCopiedToast'))
      setTimeout(() => setShareCopied(false), 2000)
    } catch {
      toast.error(t('detail.shareCopyFailedToast'))
    }
  }

  if (isLoading) return <DetailSkeleton />

  if (isError || !hotspot) {
    return (
      <div className="error-state">
        <p>{t('detail.errorMsg')}</p>
        <button className="btn btn-secondary" onClick={refetch}>
          {t('common:actions.retry')}
        </button>
      </div>
    )
  }

  return (
    <article
      className="hotspot-detail"
      aria-label={t('detail.articleLabel', {
        name: hotspotDisplayName(hotspot.name, hotspot.address, t('detail.unknownName')),
      })}
      style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
    >
      {/* Breadcrumb: Hotspots > My Hotspots > Name (SPEC hotspot-detail.html) */}
      <nav
        aria-label="Breadcrumb"
        className="htdt-breadcrumb"
        style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
      >
        <Link to="/hotspots" className="muted">
          {t('detail.breadcrumbHotspots')}
        </Link>
        {isOwner && (
          <>
            <span className="muted" aria-hidden="true">
              ›
            </span>
            <Link to="/hotspots" className="muted">
              {t('detail.breadcrumbMyHotspots')}
            </Link>
          </>
        )}
        <span className="muted" aria-hidden="true">
          ›
        </span>
        <span style={{ fontWeight: 500 }}>
          {hotspotDisplayName(hotspot.name, hotspot.address, t('detail.unknownName'))}
        </span>
      </nav>

      {/* HERO */}
      <div style={{ position: 'relative' }}>
        <HotspotHeroBar
          hotspot={hotspot}
          rank={hotspot.rank ?? null}
          chips={<HotspotStatusChips hotspot={hotspot} />}
        />
        {/* Hero-Action-Buttons — Reihenfolge gemaess SPEC hotspot-detail.html
            (+Note / Tags / Show on map / Remove); < 700 px fliessen sie unter
            den Hero (htdt-hero-actions). Edit/Share bleiben zusaetzlich
            bestehen (Task #27 / Story 1.6 AC-2 — getestete Funktionalitaet,
            im Prototyp nicht abgebildet aber ohne Ersatz-Oberflaeche). */}
        <div className="htdt-hero-actions">
          {/* +Note / Tags — Issue web#97: besitzerexklusiv (springen in den
              Notes-Tab, der fuer fremde Hotspots ohnehin ausgeblendet ist). */}
          {isOwner && (
            <>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                data-testid="add-note-hero-button"
                onClick={() => handleTabChange('notes')}
              >
                {t('detail.addNoteBtn')}
              </button>

              <button
                type="button"
                className="btn btn-secondary btn-sm"
                data-testid="tags-hero-button"
                onClick={() => handleTabChange('notes')}
              >
                {t('detail.tagsHeroBtn')}
              </button>
            </>
          )}

          {/* Show on map */}
          <Link
            to="/hotspots/map"
            className="btn btn-secondary btn-sm"
            data-testid="show-on-map-button"
          >
            {t('detail.showOnMapBtn')}
          </Link>

          {/* Story 1.6 AC-2: Link zum Speedtests-Monitor (KEIN Start-Button —
              es gibt keinen BE-Endpunkt zum Auslösen eines Speedtests) */}
          <Link
            to="/speedtest"
            className="btn btn-ghost btn-sm"
            data-testid="speedtest-results-link"
          >
            {tTabs('hero.speedtestLink')}
          </Link>

          {/* Bearbeiten — PATCH /api/v2/hotspots/{id} (UserHotspot-Felder), besitzerexklusiv */}
          {isOwner && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              aria-label={t('detail.editBtn')}
              title={t('detail.editTitle')}
              data-testid="edit-hotspot-button"
              onClick={() => setShowEditDialog(true)}
            >
              ✎ {t('detail.editBtn')}
            </button>
          )}

          {/* onboarding-wallet-hotspot Finding #7: "Speedtest starten" entfernt —
              kein BE-Endpunkt zum Ausloesen eines neuen Speedtests vorhanden
              (nur GET .../speedtests fuer historische Werte). Production-Abnahme
              erlaubt keine toten/disabled Buttons ohne Funktion dahinter. */}

          {/* Earnings teilen — clientseitig via Web Share / Clipboard */}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            aria-label={t('detail.shareBtn')}
            title={t('detail.shareTitle')}
            data-testid="share-hotspot-button"
            onClick={() => {
              void handleShare()
            }}
          >
            {shareCopied ? t('detail.shareCopied') : t('detail.shareBtn')}
          </button>

          {/* Remove (SPEC-Copy; Funktion identisch zum bisherigen "Trennen"), besitzerexklusiv */}
          {isOwner && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setShowRemoveDialog(true)}
              data-testid="remove-hotspot-button"
              aria-label={t('detail.disconnectLabel')}
              style={{ color: 'var(--danger)' }}
            >
              {t('detail.disconnectBtn')}
            </button>
          )}
        </div>
      </div>

      {/* Fix-Agent hotspot-detail (P1): SPEC hotspot-detail.html hat KEINEN
          gemeinsamen Hero-Container fuer Titel+Pills+Stats — die 3 Stat-
          Kacheln stehen als eigenstaendige Karten unter dem flachen Hero. */}
      <HotspotStat3 hotspot={hotspot} />

      {showRemoveDialog && (
        <RemoveConfirmDialog
          hotspotName={hotspotDisplayName(hotspot.name, hotspot.address, t('detail.unknownName'))}
          isPending={removeHotspot.isPending}
          onConfirm={() => {
            void handleRemove()
          }}
          onCancel={() => setShowRemoveDialog(false)}
        />
      )}

      {showEditDialog && (
        <EditHotspotDialog
          initialTagList=""
          initialWatched={false}
          initialAffiliate={false}
          isPending={updateHotspot.isPending}
          onSubmit={(values) => {
            void handleEditSubmit(values)
          }}
          onCancel={() => setShowEditDialog(false)}
        />
      )}

      {/* S10-12: DenyList Warning Banner */}
      {hotspot.denylist && (
        <div
          className="alert alert-warning"
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            padding: '12px 16px',
            background: 'var(--warning-bg, rgba(234, 179, 8, 0.1))',
            border: '1px solid var(--warning, #eab308)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            style={{ flexShrink: 0, marginTop: 2 }}
          >
            <path
              d="M10 6V10M10 14H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('denylist.heading')}</div>
            <div style={{ fontSize: 13 }}>
              {t('denylist.body')}{' '}
              <a
                href="https://docs.helium.com/blockchain/proof-of-coverage#denylist"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                {t('denylist.linkLabel')}
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Issue web#97: fremder Hotspot — dezenter Hinweis + Claim-Flow
          (POST /api/v2/hotspots, bereits genutzt von AddHotspotModal). */}
      {!isOwner && (
        <div
          className="alert alert-info"
          role="status"
          data-testid="foreign-hotspot-notice"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '10px 16px',
            background: 'var(--surface-2, rgba(148, 163, 184, 0.1))',
            border: '1px solid var(--border, #cbd5e1)',
            borderRadius: 'var(--radius-md)',
            fontSize: 13,
          }}
        >
          <span>{t('foreignHotspot.notice')}</span>
          {hotspot.address && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              data-testid="add-to-my-hotspots-button"
              disabled={addHotspot.isPending}
              onClick={() => {
                addHotspot.mutate(
                  { name_or_address: hotspot.address as string },
                  {
                    onSuccess: () => toast.success(t('foreignHotspot.addSuccessToast')),
                    onError: () => toast.error(t('foreignHotspot.addErrorToast')),
                  },
                )
              }}
            >
              {addHotspot.isPending
                ? t('foreignHotspot.addPendingBtn')
                : t('foreignHotspot.addBtn')}
            </button>
          )}
        </div>
      )}

      {/* --- Detail-Tabs (Stories 1.5 + 1.6 + finale Designvorlage) ---
          Fix-Agent hotspot-detail (P0): die vier vollen Karten, die hier
          vorher VOR den Tabs standen (Karte, Reward-Chart+Summary, Location,
          Online-Status+Activity), dupliziertem den Rewards-Tab-Inhalt und
          widersprachen der SPEC (Hero -> Stat3 -> Tabs, keine Extra-Reihen).
          Karte + redundanter Reward-Chart entfallen (SPEC hat auf dieser
          Seite keine Karte, nur den "Show on map"-Link im Hero). Location +
          Activity leben jetzt als Sidebar im Rewards-Tab (siehe grid2 unten). */}
      <div className="tabs htdt-tabs" role="tablist" aria-label={tTabs('tabs.ariaLabel')}>
        {(['rewards', 'transfer', 'splits', 'guard', 'poc', 'notes'] as const)
          .filter((key) => isOwner || !OWNER_ONLY_TABS.includes(key))
          .map((key) => (
            <button
              key={key}
              type="button"
              role="tab"
              id={`hotspot-tab-${key}`}
              aria-selected={visibleTab === key}
              aria-controls={`hotspot-panel-${key}`}
              className={`tab${visibleTab === key ? ' is-active' : ''}`}
              style={{
                background: 'none',
                border: 'none',
                borderBottom: '2px solid transparent',
                borderBottomColor: visibleTab === key ? 'var(--primary)' : 'transparent',
              }}
              data-testid={`hotspot-tab-${key}`}
              onClick={() => handleTabChange(key)}
            >
              {tTabs(`tabs.${key}`)}
            </button>
          ))}
      </div>

      {/* Tab: Data Transfer (Story 1.6, Default) */}
      {visibleTab === 'transfer' && (
        <div role="tabpanel" id="hotspot-panel-transfer" aria-labelledby="hotspot-tab-transfer">
          <DataTransferTab hotspot={hotspot} />
        </div>
      )}

      {/* Tab: PoC-Archiv (Story 1.5) */}
      {visibleTab === 'poc' && (
        <div role="tabpanel" id="hotspot-panel-poc" aria-labelledby="hotspot-tab-poc">
          <PocArchiveTab hotspotId={id} />
        </div>
      )}

      {/* Tab: Rewards & Aktivität (Default, prototype: "Rewards & Activity")
          SPEC hotspot-detail.html grid2: Chart + Recent-Activity links,
          Device/Location-Host-Sidebar rechts (kollabiert < 980px, .htdt-grid). */}
      {visibleTab === 'rewards' && (
        <div
          role="tabpanel"
          id="hotspot-panel-rewards"
          aria-labelledby="hotspot-tab-rewards"
          className="htdt-grid"
        >
          {/* Links: Reward-Verlauf + Recent Activity + Heartbeats + Speedtests */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
            <HotspotHeatmapCard />

            {/* Rewards-Verlauf */}
            <section aria-label={t('detail.rewardsLabel')}>
              <HotspotRewardsChart id={id} />
            </section>

            {/* Recent activity (SPEC: "Recent activity"-Liste unter dem Chart) */}
            <HotspotActivityCard hotspot={hotspot} />

            {/* F-036/suite#3: Heartbeat-Verlauf direkt nach dem Reward-Chart */}
            <HeartbeatHistoryCard heartbeats={hotspot.heartbeats} />

            {/* Speedtests (optional, unten) */}
            <section aria-label={t('detail.speedtestsHeading')} style={{ marginTop: 8 }}>
              <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
                {t('detail.speedtestsHeading')}
              </h2>
              <SpeedtestsTab hotspotId={id} />
            </section>
          </div>

          {/* Rechts: Device-Info + Location-Host (SPEC-Sidebar) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
            <HotspotLocationCard hotspot={hotspot} />
            {/* Issue web#108: Provisions-Regeln (HotspotSplitsCard) sind
              besitzerexklusiv — fuer fremde Hotspots faellt die Sidebar-Karte
              komplett weg, kein "Regel anlegen"-Angebot fuer Nicht-Owner. */}
            {isOwner && <HotspotSplitsCard hotspotId={hotspot.id ?? id} />}
          </div>
        </div>
      )}

      {/* Tab: Splits & Commissions — liest die echte host_split_rule (heliumtracker-web#24) */}
      {visibleTab === 'splits' && (
        <div role="tabpanel" id="hotspot-panel-splits" aria-labelledby="hotspot-tab-splits">
          {isOwner ? (
            <HotspotSplitsCard hotspotId={hotspot.id ?? id} />
          ) : (
            /* Defense-in-depth: visibleTab weicht fuer Nicht-Owner bereits auf
               "rewards" aus (Z. ~317) und der Tab-Button ist ausgeblendet
               (OWNER_ONLY_TABS), aber falls dieser Panel dennoch erreicht
               wird, kein Splits-Editor fuer fremde Hotspots. */
            <p className="muted">{t('foreignHotspot.splitsUnavailable')}</p>
          )}
        </div>
      )}

      {/* Tab: Guard — Nutzer-Schalter (guard_enabled) + Evaluator-Ergebnis (guard_status)
          bewusst in getrennt beschrifteten Bloecken. */}
      {visibleTab === 'guard' && (
        <div
          role="tabpanel"
          id="hotspot-panel-guard"
          aria-labelledby="hotspot-tab-guard"
          style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 520 }}
        >
          <HotspotGuardToggle hotspotId={id} hotspot={hotspot} />
          <div className="card card-pad" aria-label={t('guard.evaluatorCardLabel')}>
            <h3 className="section-heading" style={{ marginBottom: 4 }}>
              {t('guard.evaluatorHeading')}
            </h3>
            <p className="muted" style={{ fontSize: 12, marginTop: 0, marginBottom: 10 }}>
              {t('guard.evaluatorDescription')}
            </p>
            {/* heliumtracker-web#24: der Alarm-Status (z.B. "CRIT") wurde bisher
              unabhaengig vom Tarif angezeigt — auch mit abgelaufenem Premium
              (guard_available: false). Gleiche Gate wie beim Schalter oben:
              undefined (aelterer BE-Stand) gilt als verfuegbar. */}
            {hotspot.guard_available !== false ? (
              <GuardStatusBadge
                guard_status={hotspot.guard_status ?? undefined}
                failed_guards={hotspot.failed_guards ?? undefined}
              />
            ) : (
              <p
                className="muted"
                style={{ fontSize: 12, margin: 0 }}
                data-testid="guard-evaluator-locked"
              >
                {t('guard.evaluatorLockedHint')}
              </p>
            )}
          </div>
          {/* SPEC hotspot-detail.html: Hinweis auf den Telegram-Mute-Link */}
          <p className="muted" style={{ fontSize: 12 }} data-testid="guard-telegram-mute-note">
            {t('guard.telegramMuteNote')}
          </p>
        </div>
      )}

      {/* Tab: Notizen + Tags */}
      {visibleTab === 'notes' && (
        <div
          role="tabpanel"
          id="hotspot-panel-notes"
          aria-labelledby="hotspot-tab-notes"
          style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
        >
          <InlineNotesSection hotspotId={hotspot.id ?? id} />

          {/* Tags — F031 */}
          <section className="card card-pad" aria-label={t('detail.tagsLabel')}>
            <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>
              {t('detail.tagsHeading')}
            </h2>
            <TagPicker
              targetType="hotspot"
              targetId={hotspotNumericId}
              assignedTagIds={assignedTagIds}
            />
          </section>
        </div>
      )}
    </article>
  )
}
